const MOBILE_BREAKPOINT = '(max-width: 749px)';

class MoxieMobileHeaderController {
  #mobileQuery = window.matchMedia(MOBILE_BREAKPOINT);
  #resizeObserver = null;
  #intersectionObserver = null;
  #sentinel = null;
  #header = null;
  #headerSection = null;
  #brandSwitcherSection = null;
  #originalSectionOrder = null;

  connect() {
    this.#cacheElements();
    this.#reorderIfNeeded();
    this.#updateBrandHeight();
    this.#setupIntersectionObserver();

    this.#mobileQuery.addEventListener('change', this.#handleViewportChange);

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateBrandHeight();
    });

    if (this.#brandSwitcherSection) {
      this.#resizeObserver.observe(this.#brandSwitcherSection);
    }
  }

  disconnect() {
    this.#restoreOrder();
    this.#teardownIntersectionObserver();
    this.#mobileQuery.removeEventListener('change', this.#handleViewportChange);
    this.#resizeObserver?.disconnect();

    if (this.#header instanceof HTMLElement) {
      this.#header.classList.remove('is-mobile-condensed');
    }
  }

  #cacheElements() {
    this.#header = document.querySelector('#header-component');

    if (!(this.#header instanceof HTMLElement)) return;

    this.#headerSection = this.#header.closest('.header-section');
    this.#brandSwitcherSection = document.querySelector('#header-group > .shopify-section:has(.brand-switcher-component)');
  }

  #reorderIfNeeded = () => {
    if (this.#mobileQuery.matches) {
      this.#reorderForMobile();
    } else {
      this.#restoreOrder();
    }
  };

  #reorderForMobile = () => {
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup || headerGroup.hasAttribute('data-mobile-ordered')) return;

    this.#cacheElements();

    const brandSection = this.#brandSwitcherSection;
    if (!brandSection) return;

    const announcementSection = headerGroup.querySelector(':scope > .shopify-section:has(.announcement-bar)');

    this.#originalSectionOrder = [...headerGroup.children];
    headerGroup.prepend(brandSection);
    if (announcementSection) {
      headerGroup.append(announcementSection);
    }
    headerGroup.setAttribute('data-mobile-ordered', '');
  };

  #restoreOrder = () => {
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup || !this.#originalSectionOrder) return;

    for (const node of this.#originalSectionOrder) {
      headerGroup.appendChild(node);
    }

    this.#originalSectionOrder = null;
    headerGroup.removeAttribute('data-mobile-ordered');
  };

  #updateBrandHeight = () => {
    this.#cacheElements();

    if (!(this.#header instanceof HTMLElement)) return;

    if (!this.#mobileQuery.matches) {
      document.documentElement.style.removeProperty('--moxie-mobile-brand-switcher-height');
      this.#header.classList.remove('is-mobile-condensed');
      return;
    }

    const brandHeight = this.#brandSwitcherSection?.offsetHeight ?? 0;
    document.documentElement.style.setProperty('--moxie-mobile-brand-switcher-height', `${brandHeight}px`);
  };

  #setupIntersectionObserver = () => {
    this.#teardownIntersectionObserver();

    if (!this.#mobileQuery.matches) return;

    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup || !(this.#header instanceof HTMLElement)) return;

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:4px;pointer-events:none;visibility:hidden;';
    headerGroup.prepend(sentinel);
    this.#sentinel = sentinel;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!(this.#header instanceof HTMLElement)) return;
        this.#header.classList.toggle('is-mobile-condensed', !entry.isIntersecting);
      },
      { threshold: [0] }
    );

    this.#intersectionObserver.observe(sentinel);
  };

  #teardownIntersectionObserver = () => {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#sentinel?.remove();
    this.#sentinel = null;
  };

  #handleViewportChange = () => {
    this.#reorderIfNeeded();
    this.#updateBrandHeight();
    this.#setupIntersectionObserver();
  };
}

if (window.__moxieMobileHeaderController) {
  window.__moxieMobileHeaderController.disconnect();
}

window.__moxieMobileHeaderController = new MoxieMobileHeaderController();
window.__moxieMobileHeaderController.connect();
